import { alert } from '@/services/alert';
import { PartnerSummarySkeleton } from '@/components/TimelineSkeleton';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Flex, Stack, Text } from '@mantine/core';
import { IconFileText, IconListDetails } from '@tabler/icons-react';
import 'react-h5-audio-player/lib/styles.css';
import { usePartner } from './context-hooks/usePartner';
import { PartnerTimelineHeader } from './PartnerTimelineHeader';
import { PartnerTimelineSummary } from './PartnerTimelineSummary';
import { PartnerTimelineContent } from './PartnerTimelineContent';
import { TimelineContext } from '@/contexts/timelineContexts';
import { DocumentsCard } from '@/components/Timeline/Documents';
import { useMessageBox } from '@/pages/PatientTimelinePage/context-hooks/useMessageBox';
import { usePartnerTimeline } from './context-hooks/useTimeline';
import { useSearchParams } from 'react-router';
import { TimelineTabs, TabOption } from '@/components/Timeline/TimelineTabs';
import { useCurrentUser } from '@/hooks/useCurrentUser';

enum TabType {
  DETAILS = 'details',
  DOCUMENTS = 'documents',
}

const TABS: TabOption<TabType>[] = [
  { label: 'Details', icon: <IconListDetails size={14} />, value: TabType.DETAILS },
  { label: 'Documents', icon: <IconFileText size={14} />, value: TabType.DOCUMENTS },
];

export function PartnerTimelinePage(): ReactElement {
  const { currentUserPermissions, permissionsLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [searchParams] = useSearchParams();
  const partnerContext = usePartner(organizationId);
  const { organization, organizationLoading, organizationError, updateOrganization } = partnerContext;

  const timelineContext = usePartnerTimeline({
    organization,
    urlSearchParams: searchParams,
  });

  const messageBoxContext = useMessageBox();

  const [activeTab, setActiveTab] = useState<TabType>(TabType.DETAILS);

  const partnerContextValue = useMemo(
    () => ({
      partnerContext,
      timelineContext,
      messageBoxContext,
    }),
    [partnerContext, timelineContext, messageBoxContext]
  );

  useEffect(() => {
    if (!organizationLoading && organizationError) {
      alert.error('An error occurred while loading the organization.');
    }
  }, [organizationError, organizationLoading]);

  useEffect(() => {
    if (!permissionsLoading && !currentUserPermissions.canAccessPartners) {
      alert.error({
        title: 'Access denied',
        message: (
          <Text>
            You don't have permission to view this page.
            <br />
            If you think this is a mistake, contact your administrator.
          </Text>
        ),
      });
      navigate('/', { replace: true });
    }
  }, [permissionsLoading, currentUserPermissions.canAccessPartners, navigate]);

  if (permissionsLoading || !currentUserPermissions.canAccessPartners) {
    return <></>;
  }

  return (
    <TimelineContext.Provider value={partnerContextValue}>
      <PartnerTimelineHeader organization={organization} />

      <Flex direction={{ base: 'column', xs: 'row' }} align="stretch" wrap="nowrap">
        {/* Left sidebar - Summary */}
        <Stack
          w={{ base: '100%', xs: '28%' }}
          maw={{ xs: '380px' }}
          style={{ borderRight: '1px solid var(--mantine-color-gray-3)' }}
          p="md"
        >
          <TimelineTabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

          {activeTab === TabType.DETAILS &&
            (organizationLoading ? (
              <PartnerSummarySkeleton />
            ) : organization ? (
              <PartnerTimelineSummary
                organization={organization}
                onOrganizationChange={(updatedOrg) => updateOrganization(updatedOrg)}
              />
            ) : (
              <Stack gap="xs" p="md">
                <Text size="sm" c="dimmed">
                  Organization not found
                </Text>
              </Stack>
            ))}
          {activeTab === TabType.DOCUMENTS &&
            (organizationLoading ? (
              <PartnerSummarySkeleton />
            ) : organization ? (
              <DocumentsCard resource={organization} />
            ) : (
              <Stack gap="xs" p="md">
                <Text size="sm" c="dimmed">
                  Organization not found
                </Text>
              </Stack>
            ))}
        </Stack>

        {/* Right side - Timeline */}
        <PartnerTimelineContent />
      </Flex>
    </TimelineContext.Provider>
  );
}
